// src/services/notifications.js
// Faz 1C Görev 6 — In-app bildirim altyapısı.
//
// Tek kaynak writer + reader API. Mevcut `/notifications/{id}` collection
// yapısını korur; rules (firestore.rules:289-301) uyumlu payload üretir.
//
// Dedup: Deterministik doc id ile aynı engagement için spam önlenir.
//   - like:    notifications/like_{contentId}_{fromUid}
//   - comment: notifications/comment_{contentId}_{commentId}
//   - reply:   notifications/reply_{contentId}_{commentId}_{replyId}
//   - mention: notifications/mention_{contentId}_{commentId}_{fromUid}
//   - follow:  notifications/follow_{toUid}_{fromUid}
//
// Fire-and-forget semantiği: yorum/yanıt yazma akışını bildirim hatası
// bozmasın — hata sessizce log'lanır, çağıran path'i etkilemez.

import { auth, db } from "../firebase";
import { useEffect, useState } from "react";
import type { FieldValue } from "firebase/firestore";
import { hataMesaji } from "../utils/hataAlani";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  getDocs,
  limit as fsLimit,
} from "firebase/firestore";
import {
  NOTIFICATION_TYPE,
  NOTIFICATION_PREVIEW_MAX_LENGTH,
} from "../constants/myClipsSchema";

// ─── yardımcılar ──────────────────────────────────────────────────────────
/**
 * BİLDİRİM — İKİ ŞEKİL, ÇÜNKÜ GÖVDE İKİ FARKLI ŞEY YAPIYOR.
 *
 * ⚠️ #796'DA TEK TİP YAZMIŞTIM VE YANLIŞTI; DERLEYİCİ İKİ KUSURU BİRDEN
 * GÖSTERDİ (bu tur, üretici tiplenince):
 *   (a) `createdAt` YAZARKEN `serverTimestamp()` → `FieldValue`,
 *       OKURKEN Firestore `Timestamp`. Tek alanla ikisini birden söylemek
 *       imkânsız; ben okuma şeklini yazıp yazma yoluna dayatmıştım.
 *   (b) `replyId` alanını HİÇ saymamıştım — `notifyClipReply` yazıyor.
 *       Yani #796'daki tip, doğduğu turda zaten eksikti.
 *
 * 🧭 KANON: *bir belgenin yazma şekli ile okuma şekli aynı tip değildir.*
 *    `serverTimestamp()` bunun en görünür kanıtı: yazarken bir KOMUT,
 *    okurken bir DEĞER.
 *
 * Alanlar opsiyonel: belgeler farklı `notify*` yollarından geliyor ve hepsi
 * aynı alan kümesini yazmıyor (örn. `preview` yalnız yorum/yanıtta var).
 */
type BildirimOrtak = {
  id?: string;
  to?: string;
  from?: string;
  read?: boolean;
  type?: string;
  text?: string;
  fromUsername?: string;
  fromAvatar?: string;
  contentId?: string;
  contentType?: string;
  commentId?: string;
  replyId?: string;
  preview?: string;
  link?: string;
  clipId?: string;
  postId?: string;
};

/** OKUMA şekli — `Bildirimler.tsx` bunu tüketiyor (`createdAt` bir DEĞER). */
export type Bildirim = BildirimOrtak & {
  createdAt?: { toDate?: () => Date } | Date | null;
};

/** YAZMA şekli — `serverTimestamp()` bir KOMUT (`FieldValue`), değer değil. */
export type BildirimYazimi = BildirimOrtak & {
  createdAt?: FieldValue;
};

/**
 * Yazıcıların ORTAK dönüşü. Sekiz `notify*` fonksiyonu ve üç `mark*`
 * fonksiyonu bu şekli döndürüyordu ve her biri kendi JSDoc'unda
 * `@returns {Promise<{ok:boolean}>}` diye ELLE sayıyordu — sekiz kopya,
 * hiçbiri derlenmiyor. Ölçüldü: JSDoc'lar `reason`/`id`/`error`/`marked`
 * alanlarını HİÇ saymıyordu, yani sekizi de gövdeden ayrışmıştı.
 *
 * 🧭 KANON: *derlenmeyen bir sözleşme, sözleşme değildir.*
 */
export type YazmaSonucu = {
  ok: boolean;
  reason?: string;
  id?: string;
  error?: string;
  marked?: number;
};

/** `currentAuthorSnapshot` çıktısı — payload'ların `from*` alanlarını besler. */
type YazarAnligi = { uid: string; username: string; avatar: string };

function currentUid() {
  return auth?.currentUser?.uid || null;
}

function currentAuthorSnapshot(): YazarAnligi {
  const user = (auth?.currentUser || {}) as any;
  const displayName =
    user.displayName || (user.email ? String(user.email).split("@")[0] : "kullanıcı");
  return {
    uid: user.uid || "",
    username: displayName,
    avatar: user.photoURL || "",
  };
}

function trimPreview(text?: unknown) {
  const t = String(text || "").trim();
  if (t.length <= NOTIFICATION_PREVIEW_MAX_LENGTH) return t;
  return `${t.slice(0, NOTIFICATION_PREVIEW_MAX_LENGTH - 1)}…`;
}

// Firestore doc ID safe karakterler: forward-slash ve tek uzunluk sınırı (1500).
// UID'ler + kısa prefix güvende; yine de özel karakterleri replace edelim.
function safeIdPart(s?: unknown) {
  return String(s || "").replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 120);
}

function logWarn(label: string, err?: unknown) {
  try {
    // eslint-disable-next-line no-console
    console.warn(`[notifications] ${label}:`, hataMesaji(err) || err);
  } catch {}
}

// ─── ortak writer ─────────────────────────────────────────────────────────
/**
 * `/notifications/{docId}` path'ine bildirim yazar. Dedup için deterministik
 * docId ile `setDoc()` kullanır (rules create check'i `read === false` +
 * `from === auth.uid` sağlar; same-key tekrar yazımı Firestore aynı create
 * olarak ele alır — eski doc yerine yenisi geçer).
 *
 * Not: Caller'a hata fırlatmaz — fire-and-forget semantik. Dönüş: `{ok: bool}`.
 */
async function writeNotification({ docId, payload }: {
  docId?: string;
  payload?: BildirimYazimi;
}): Promise<YazmaSonucu> {
  if (!docId || !payload) return { ok: false };
  try {
    const ref = doc(collection(db, "notifications"), docId);
    await setDoc(ref, payload);
    return { ok: true, id: docId };
  } catch (err) {
    logWarn(`writeNotification(${docId})`, err);
    return { ok: false, error: hataMesaji(err) || String(err) };
  }
}

// ─── PUBLIC API — TYPE-SPECIFIC WRITERS ───────────────────────────────────

/**
 * Clip yoruma bildirim. Clip sahibini tetikler.
 *
 */
export async function notifyClipComment({ clipId, commentId, authorId, text }: {
  clipId?: string; commentId?: string; authorId?: string; text?: string;
}): Promise<YazmaSonucu> {
  const from = currentAuthorSnapshot();
  if (!from.uid) return { ok: false, reason: "no_auth" };
  if (!clipId || !commentId || !authorId) return { ok: false, reason: "missing_args" };
  // Kendi yorumuna bildirim gönderme
  if (authorId === from.uid) return { ok: false, reason: "self" };

  const docId = `${NOTIFICATION_TYPE.COMMENT}_${safeIdPart(clipId)}_${safeIdPart(commentId)}`;
  const payload = {
    to: authorId,
    from: from.uid,
    read: false,
    createdAt: serverTimestamp(),
    type: NOTIFICATION_TYPE.COMMENT,
    text: "klibine yorum yaptı.",
    fromUsername: from.username,
    fromAvatar: from.avatar,
    contentId: clipId,
    contentType: "clip",
    commentId,
    preview: trimPreview(text),
    link: `#clip/${clipId}?cid=${commentId}`,
  };
  return writeNotification({ docId, payload });
}

/**
 * Yoruma yanıt bildirimi. Parent yorumun sahibini tetikler.
 *
 */
export async function notifyClipReply({
  clipId,
  parentCommentId,
  replyId,
  parentAuthorId,
  text,
}: {
  clipId?: string; parentCommentId?: string; replyId?: string;
  parentAuthorId?: string; text?: string;
}): Promise<YazmaSonucu> {
  const from = currentAuthorSnapshot();
  if (!from.uid) return { ok: false, reason: "no_auth" };
  if (!clipId || !parentCommentId || !replyId || !parentAuthorId) {
    return { ok: false, reason: "missing_args" };
  }
  if (parentAuthorId === from.uid) return { ok: false, reason: "self" };

  const docId = `${NOTIFICATION_TYPE.REPLY}_${safeIdPart(clipId)}_${safeIdPart(parentCommentId)}_${safeIdPart(replyId)}`;
  const payload = {
    to: parentAuthorId,
    from: from.uid,
    read: false,
    createdAt: serverTimestamp(),
    type: NOTIFICATION_TYPE.REPLY,
    text: "yorumuna yanıt verdi.",
    fromUsername: from.username,
    fromAvatar: from.avatar,
    contentId: clipId,
    contentType: "clip",
    commentId: parentCommentId,
    replyId,
    preview: trimPreview(text),
    link: `#clip/${clipId}?cid=${parentCommentId}&rid=${replyId}`,
  };
  return writeNotification({ docId, payload });
}

/**
 * Mention bildirimi. Yorum veya reply metninde @username ile çağrılan
 * kullanıcıya tetiklenir. Dedup: `mention_{clipId}_{commentId}_{mentionedUid}`
 * (aynı comment içinde aynı kişiyi birden çok kez bahsetmek tek bildirim).
 *
 */
export async function notifyMention({
  clipId,
  commentId,
  replyId,
  mentionedUid,
  text,
}: {
  clipId?: string; commentId?: string; replyId?: string;
  mentionedUid?: string; text?: string;
}): Promise<YazmaSonucu> {
  const from = currentAuthorSnapshot();
  if (!from.uid) return { ok: false, reason: "no_auth" };
  if (!clipId || !commentId || !mentionedUid) {
    return { ok: false, reason: "missing_args" };
  }
  // Kendini mention'a bildirim yazma
  if (mentionedUid === from.uid) return { ok: false, reason: "self" };

  const docId = `${NOTIFICATION_TYPE.MENTION}_${safeIdPart(clipId)}_${safeIdPart(commentId)}_${safeIdPart(mentionedUid)}`;
  const isReply = !!replyId;
  const link = isReply
    ? `#clip/${clipId}?cid=${commentId}&rid=${replyId}`
    : `#clip/${clipId}?cid=${commentId}`;

  const payload = {
    to: mentionedUid,
    from: from.uid,
    read: false,
    createdAt: serverTimestamp(),
    type: NOTIFICATION_TYPE.MENTION,
    text: isReply ? "bir yanıtta senden bahsetti." : "bir yorumda senden bahsetti.",
    fromUsername: from.username,
    fromAvatar: from.avatar,
    contentId: clipId,
    contentType: "clip",
    commentId,
    ...(replyId ? { replyId } : {}),
    preview: trimPreview(text),
    link,
  };
  return writeNotification({ docId, payload });
}

/**
 * Post yorumu bildirimi (IG paritesi). Gönderi sahibini tetikler — bu yol
 * bugüne kadar HİÇ yoktu (yalnız klip yorumları bildirim üretiyordu).
 * Dedup: `comment_{postId}_{commentId}`.
 *
 */
export async function notifyPostComment({ postId, commentId, authorId, text }: {
  postId?: string; commentId?: string; authorId?: string; text?: string;
}): Promise<YazmaSonucu> {
  const from = currentAuthorSnapshot();
  if (!from.uid) return { ok: false, reason: "no_auth" };
  if (!postId || !commentId || !authorId) return { ok: false, reason: "missing_args" };
  if (authorId === from.uid) return { ok: false, reason: "self" };

  const docId = `${NOTIFICATION_TYPE.COMMENT}_${safeIdPart(postId)}_${safeIdPart(commentId)}`;
  const payload = {
    to: authorId,
    from: from.uid,
    read: false,
    createdAt: serverTimestamp(),
    type: NOTIFICATION_TYPE.COMMENT,
    text: "gönderine yorum yaptı.",
    fromUsername: from.username,
    fromAvatar: from.avatar,
    contentId: postId,
    contentType: "post",
    commentId,
    preview: trimPreview(text),
    // 🔴 ESKİDEN `/p/${postId}`ydi — YORUM ÇAPASI DÜŞÜYORDU (ölçüldü 2026-08-24).
    // "Gönderine yorum yaptı" bildirimine dokunan kullanıcı gönderiyi açıyor
    // ama HANGİ yoruma geldiğini bulamıyordu; 300 yorumlu bir gönderide bu,
    // bildirimin işe yaramaması demek.
    //
    // Çapa zinciri ZATEN TAMDI, yalnız ÜRETİCİSİ yoktu:
    //   · router matcher    → appRouting.ts `mPost` /p/{id}/c/{cid}[/r/{rid}]
    //   · kaydırma + vurgu  → PostDetailModalDesktop `anchored`
    //   · sunucu paylaşımı  → post_og.ts commentId → `&cm=` ile geri veriyor
    // Kod tabanında bu şemayı ÜRETEN tek satır yoktu → ölü rota.
    // `commentId` bu payload'da zaten vardı; artık link de taşıyor.
    link: `/p/${postId}/c/${commentId}`,
  };
  return writeNotification({ docId, payload });
}

/**
 * Hikaye mention bildirimi (IG paritesi — hikaye sticker'ında @username).
 * Dedup: `mention_story_{storyId}_{mentionedUid}`. Hikaye permalink'i
 * olmadığından link yazarın profiline gider (hikaye halkada bulunur).
 *
 */
export async function notifyStoryMention({ storyId, mentionedUid, authorUsername }: {
  storyId?: string; mentionedUid?: string; authorUsername?: string;
}): Promise<YazmaSonucu> {
  const from = currentAuthorSnapshot();
  if (!from.uid) return { ok: false, reason: "no_auth" };
  if (!storyId || !mentionedUid) return { ok: false, reason: "missing_args" };
  if (mentionedUid === from.uid) return { ok: false, reason: "self" };

  const docId = `${NOTIFICATION_TYPE.MENTION}_story_${safeIdPart(storyId)}_${safeIdPart(mentionedUid)}`;
  const payload = {
    to: mentionedUid,
    from: from.uid,
    read: false,
    createdAt: serverTimestamp(),
    type: NOTIFICATION_TYPE.MENTION,
    text: "bir hikayede senden bahsetti.",
    fromUsername: from.username,
    fromAvatar: from.avatar,
    contentId: storyId,
    contentType: "story",
    link: `/${String(authorUsername || from.username || "").toLowerCase()}`,
  };
  return writeNotification({ docId, payload });
}

/**
 * Post caption mention bildirimi (IG paritesi — gönderi açıklamasında
 * @username). Dedup: `mention_post_{postId}_{mentionedUid}` — aynı postta
 * aynı kişi tek bildirim. Link permalink'e gider (/p/{postId}).
 *
 */
export async function notifyPostMention({ postId, mentionedUid, text }: {
  postId?: string; mentionedUid?: string; text?: string;
}): Promise<YazmaSonucu> {
  const from = currentAuthorSnapshot();
  if (!from.uid) return { ok: false, reason: "no_auth" };
  if (!postId || !mentionedUid) return { ok: false, reason: "missing_args" };
  if (mentionedUid === from.uid) return { ok: false, reason: "self" };

  const docId = `${NOTIFICATION_TYPE.MENTION}_post_${safeIdPart(postId)}_${safeIdPart(mentionedUid)}`;
  const payload = {
    to: mentionedUid,
    from: from.uid,
    read: false,
    createdAt: serverTimestamp(),
    type: NOTIFICATION_TYPE.MENTION,
    text: "bir gönderide senden bahsetti.",
    fromUsername: from.username,
    fromAvatar: from.avatar,
    contentId: postId,
    contentType: "post",
    preview: trimPreview(text),
    link: `/p/${postId}`,
  };
  return writeNotification({ docId, payload });
}

/**
 * KİŞİ ETİKETLEME (posts) — gönderide etiketlenen kullanıcıya bildirim.
 * Clips tag bildiriminin post karşılığı; pending=true ise onay isteği
 * (tag_request — hedef tagReviewEnabled açmış).
 *
 */
export async function notifyPostTag({ postId, taggedUid, pending = false }: {
  postId?: string; taggedUid?: string; pending?: boolean;
}): Promise<YazmaSonucu> {
  const from = currentAuthorSnapshot();
  if (!from.uid) return { ok: false, reason: "no_auth" };
  if (!postId || !taggedUid) return { ok: false, reason: "missing_args" };
  if (taggedUid === from.uid) return { ok: false, reason: "self" };

  const type = pending ? "tag_request" : "tag";
  const docId = `${type}_post_${safeIdPart(postId)}_${safeIdPart(taggedUid)}`;
  const payload = {
    to: taggedUid,
    from: from.uid,
    read: false,
    createdAt: serverTimestamp(),
    type,
    text: pending
      ? "seni bir gönderide etiketlemek istiyor."
      : "seni bir gönderide etiketledi.",
    fromUsername: from.username,
    fromAvatar: from.avatar,
    contentId: postId,
    contentType: "post",
    link: `/p/${postId}`,
  };
  return writeNotification({ docId, payload });
}

/**
 * Clip sahibi bildirim için denormalize authorId fetch. Service layer
 * notifyClipComment'i çağırmadan önce bu helper ile clip.authorId alır.
 * Hata durumunda null döner — caller bildirim yazımını atlar.
 */
export async function fetchClipAuthorId(clipId?: string) {
  if (!clipId) return null;
  try {
    const snap = await getDoc(doc(db, "clips", clipId));
    if (!snap.exists()) return null;
    return snap.data()?.authorId || null;
  } catch (err) {
    logWarn(`fetchClipAuthorId(${clipId})`, err);
    return null;
  }
}

/**
 * Like bildirimi (clip veya post). İçerik sahibine tetiklenir.
 * Dedup: `like_{contentId}_{fromUid}` — aynı kullanıcı unlike → like toggle'ı
 * yaparsa tek doc overwrite olur (unread rozeti şişmez).
 *
 * Faz 1D Görev 2 — Feed.js inline writer'ından taşındı.
 *
 */
export async function notifyLike({ contentId, contentType = "clip", authorId, text }: {
  contentId?: string; contentType?: string; authorId?: string; text?: string;
}): Promise<YazmaSonucu> {
  const from = currentAuthorSnapshot();
  if (!from.uid) return { ok: false, reason: "no_auth" };
  if (!contentId || !authorId) return { ok: false, reason: "missing_args" };
  if (authorId === from.uid) return { ok: false, reason: "self" };

  const docId = `${NOTIFICATION_TYPE.LIKE}_${safeIdPart(contentId)}_${safeIdPart(from.uid)}`;
  // KİMLİK: kalp/beğeni YOK — engagement = 5★ puanlama. Metin "beğendi" değil
  // "puan verdi" (altyazı begenenler dizisi ama anlam = değerlendirme).
  const defaultText = contentType === "post" ? "gönderini puanladı." : "klibini puanladı.";
  const payload = {
    to: authorId,
    from: from.uid,
    read: false,
    createdAt: serverTimestamp(),
    type: NOTIFICATION_TYPE.LIKE,
    text: text || defaultText,
    fromUsername: from.username,
    fromAvatar: from.avatar,
    contentId,
    contentType,
    link: contentType === "post" ? `#post/${contentId}` : `#clip/${contentId}`,
  };
  return writeNotification({ docId, payload });
}

// INSTAGRAM-P0-4 — notifyFollow KALDIRILDI (ölü koddu, 0 caller).
// "Seni takip etmeye başladı" bildirimi artık server'da üretiliyor:
// functions/src/index.ts onFollowersCreate → notifications/follow_{to}_{from}.
// Tüm follow yolları (FollowButton, öneriler, wizard, MyClips rail,
// istek kabulü) edge yazımı üzerinden otomatik kapsanır.

/**
 * Reply için parent comment'in authorId'sini çek.
 */
export async function fetchClipCommentAuthorId(clipId?: string, commentId?: string) {
  if (!clipId || !commentId) return null;
  try {
    const ref = doc(db, "content", `clip:${clipId}`, "comments", String(commentId));
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return snap.data()?.authorId || null;
  } catch (err) {
    logWarn(`fetchClipCommentAuthorId(${clipId}/${commentId})`, err);
    return null;
  }
}

// ─── PUBLIC API — READER / MARK-READ ──────────────────────────────────────

// ─── TEK KAYNAK — okunmamış bildirim rozeti semantiği ─────────────────────
// Çan (bell) rozeti SADECE şu bildirimleri sayar:
//   to == uid && read == false && type != "dm"
// DM bildirimleri (type:"dm") ayrı bir yüzeydir (gelen-kutusu okunmamış
// rozeti, watchMyConversations.hasUnread). Bunları çan rozetine KATMAK
// "rozette N, panelde N-1" sürüklenmesine yol açıyordu (LogoBar/Bildirimler
// zaten dm'i eliyordu; watchUnreadNotificationCount + useNotifications.unreadCount
// elemiyordu → 3 farklı sayı). Artık tüm tüketiciler bu helper'dan geçer.
//
// hiddenSet verilirse (engellenen/sessize alınan aktörler) onlardan gelenler
// de sayılmaz — LogoBar'ın eski davranışı korunur.
export function isBellNotification(data: any): boolean {
  return !!data && data.type !== "dm";
}

export function countUnreadBellDocs(
  docs: Array<{ data: () => any }>,
  hiddenSet?: Set<string> | null,
): number {
  let n = 0;
  for (const d of docs) {
    const data = d.data?.() || {};
    if (!isBellNotification(data)) continue;
    const from = String(data.from || "");
    if (hiddenSet && hiddenSet.size > 0 && from && hiddenSet.has(from)) continue;
    // 🐞 BUGGIFY · i-can-rozeti-cift-sayar — AYNI BELGEYİ İKİ KEZ SAY.
    // Taklit edilen GERÇEK arıza: bildirim yazımı AT-LEAST-ONCE'tır (CLAUDE.md
    // "notification double-trigger + token-store birleştirme" borcu) → aynı
    // olay için iki `notifications` dokümanı doğabilir ve rozet gerçekte
    // olmayan bir sayıyı gösterir. SINANAN ONARIM: rozetin `BELL_CAP` sınırı
    // ve "99+" gösterimi — sayı şişse bile kullanıcıya anlamsız bir rakam
    // ÇIKMAMALI. ⚙️ Üretimde derleme zamanı elenir (`false && …` → Terser).
    if (process.env.REACT_APP_BUGGIFY === "1" && window.__vwBuggify?.("i-can-rozeti-cift-sayar")) n += 1;
    n += 1;
  }
  return n;
}

/**
 * Okunmamış (çan) bildirim sayacını canlı izler. TEK KAYNAK — tüm rozet
 * tüketicileri bunu (veya useUnreadNotificationCount hook'unu) kullanır.
 * Sorgu yalnız `to==uid && read==false` (composite index yok); `type=='dm'`
 * client-side elenir → ekstra index gerekmez, DM ayrı yüzeyde sayılır.
 *
 */
export function watchUnreadNotificationCount(
  onChange: (n: number) => void,
  opts: { hiddenSet?: Set<string> | null } = {},
) {
  if (typeof onChange !== "function") return () => {};
  const uid = currentUid();
  if (!uid) {
    onChange(0);
    return () => {};
  }

  // FAZ 2 (3M ölçek): rozet sorgusu SINIRSIZDI → 50k birikmiş okunmamışı olan
  // hesap her açılışta hepsini stream ediyordu (mobil OOM + 50k billed read).
  // Rozet zaten yaklaşık ("çok" → "99+"); canlı listener'ı `BELL_CAP` ile
  // sınırla. Eşitlik filtreleri + limit → composite index GEREKMEZ (deterministik
  // sıra olmadan da rozet sayımı için yeterli; tam okunmamış listesi ayrı sayfada).
  const BELL_CAP = 100;
  const q = query(
    collection(db, "notifications"),
    where("to", "==", uid),
    where("read", "==", false),
    fsLimit(BELL_CAP)
  );
  return onSnapshot(
    q,
    // 🐞 BUGGIFY · i-can-rozeti-siniri-asar — SAYIYI CAP'İN ÜSTÜNE ÇIKAR.
    // Taklit edilen GERÇEK arıza: rozet sayısı bir gün SUNUCUDAN denorm
    // alanla gelebilir (bu dosyanın kendi FAZ 2 notu 50k birikmiş okunmamıştan
    // söz ediyor) ya da `BELL_CAP` bir regresyonla kalkabilir. SINANAN ONARIM:
    // TÜKETİCİ tarafı — rozet "99+" ile kırpmalı, ham 700 sayısı basmamalı ve
    // düzen bozulmamalı. ⚙️ Üretimde derleme zamanı elenir.
    (snap) => onChange(
      (process.env.REACT_APP_BUGGIFY === "1" && window.__vwBuggify?.("i-can-rozeti-siniri-asar") ? 7 : 1)
      * countUnreadBellDocs(snap.docs, opts.hiddenSet) + (process.env.REACT_APP_BUGGIFY === "1" && window.__vwBuggify?.("i-can-rozeti-siniri-asar") ? 700 : 0),
    ),
    (err) => {
      logWarn("watchUnreadNotificationCount", err);
      onChange(0);
    }
  );
}

/**
 * React hook — çan rozeti okunmamış sayısı. watchUnreadNotificationCount'ın
 * tek-kaynak semantiğini (type!='dm' + opsiyonel hiddenSet) hook olarak verir.
 * uid değişiminde re-subscribe; hiddenSet referansı değişince yeniden hesaplar.
 */
export function useUnreadNotificationCount(hiddenSet?: Set<string> | null): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const unsub = watchUnreadNotificationCount(setCount, { hiddenSet });
    return () => {
      try { unsub?.(); } catch { /* no-op */ }
    };
    // hiddenSet referansı her render'da yeniyse useHiddenUids zaten memoize
    // ediyor; size'ı dep'e ekleyip gereksiz re-subscribe'ı kısıtlıyoruz.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenSet]);
  return count;
}

/**
 * Kullanıcının bildirimlerini zaman ters sırasıyla canlı izler (rozet ve
 * panel aynı snapshot'u kullanabilir). Bildirimler.js bu fonksiyonu çağırır.
 *
 */
export function watchMyNotifications(
  onChange?: (items: Bildirim[]) => void,
  { limit = 50 }: { limit?: number } = {},
): () => void {
  if (typeof onChange !== "function") return () => {};
  const uid = currentUid();
  if (!uid) {
    onChange([]);
    return () => {};
  }
  const q = query(
    collection(db, "notifications"),
    where("to", "==", uid),
    orderBy("createdAt", "desc"),
    fsLimit(limit)
  );
  // DENETİM FIX — fallback listener'ın unsubscribe'ı error callback'in
  // return'ünde KAYBOLUYORDU: caller'ın elindeki unsub ölü primary'yi
  // kapatıyor, fallback oturum boyu sızıyordu (panel her açılışta kalıcı
  // listener + okuma maliyeti). Artık tek unsub ikisini de kapatır.
  let fallbackUnsub: (() => void) | null = null;
  const primaryUnsub = onSnapshot(
    q,
    (snap) => {
      const items = (snap?.docs || []).map((d) => ({ id: d.id, ...(d.data() || {}) }));
      onChange(items);
    },
    (err) => {
      // orderBy'li query index gerektirebilir — fallback: sırasız fetch.
      logWarn("watchMyNotifications(primary)", err);
      try {
        const qFallback = query(
          collection(db, "notifications"),
          where("to", "==", uid)
        );
        fallbackUnsub = onSnapshot(qFallback, (snap) => {
          const items = (snap?.docs || []).map((d) => ({ id: d.id, ...(d.data() || {}) }));
          items.sort((a, b) => {
            const da = (a as any).createdAt?.toDate?.() || 0;
            const dbb = (b as any).createdAt?.toDate?.() || 0;
            return dbb - da;
          });
          onChange(items.slice(0, limit));
        }, (fbErr) => {
          logWarn("watchMyNotifications(fallback-listener)", fbErr);
          onChange([]);
        });
      } catch (fallbackErr) {
        logWarn("watchMyNotifications(fallback)", fallbackErr);
        onChange([]);
      }
    }
  );
  return () => {
    try { primaryUnsub(); } catch { /* no-op */ }
    try { fallbackUnsub?.(); } catch { /* no-op */ }
  };
}

/**
 * Tek bildirimi okundu işaretle.
 */
export async function markNotificationRead(notifId?: string): Promise<YazmaSonucu> {
  if (!notifId) return { ok: false };
  try {
    await updateDoc(doc(db, "notifications", String(notifId)), { read: true });
    return { ok: true };
  } catch (err) {
    logWarn(`markNotificationRead(${notifId})`, err);
    return { ok: false };
  }
}

/**
 * Toplu okundu işaretleme — panel açıldığında çağrılabilir.
 * Batch limit 500; güvenli marj 400.
 */
export async function markAllNotificationsRead() {
  const uid = currentUid();
  if (!uid) return { ok: false, marked: 0 };
  try {
    const q = query(
      collection(db, "notifications"),
      where("to", "==", uid),
      where("read", "==", false),
      fsLimit(400)
    );
    const snap = await getDocs(q);
    if (snap.empty) return { ok: true, marked: 0 };
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.update(d.ref, { read: true }));
    await batch.commit();
    return { ok: true, marked: snap.size };
  } catch (err) {
    logWarn("markAllNotificationsRead", err);
    return { ok: false, marked: 0 };
  }
}

/**
 * DM okundu → ilgili DM bildirimlerini de okundu işaretle.
 *
 * Bir DM thread'i açılıp/okununca (dm.markRead → lastReadAt), o thread'e ait
 * `/notifications/{dm_{conversationId}_{messageId}}` doc'ları read=false
 * kalıyordu → çan rozeti DM'i saymasa bile bu doc'lar kalıcı birikiyor
 * (panel "Okunmamış" sekmesinde + ileride dm rozete katılırsa şişme). Bu
 * helper o thread'in açılmamış DM bildirimlerini batch'le okundu yapar.
 *
 * Eşleştirme: DM bildirim docId'si server'da (dm_message_push.ts)
 * `dm_{conversationId}_{messageId}` deseniyle yazılır → conversationId'yi
 * docId prefix'inden eşleriz (doc üzerinde ayrı conversationId alanı yok).
 * Sorgu `to==uid && read==false` (mevcut index); type=='dm' + prefix
 * client-side süzülür → EKSTRA INDEX GEREKMEZ, alakasız bildirim işaretlenmez.
 *
 * Fire-and-forget — DM okuma akışını bloklamaz/kırmaz.
 */
export async function markDmNotificationsRead(conversationId?: string): Promise<YazmaSonucu> {
  const uid = currentUid();
  if (!uid || !conversationId) return { ok: false, marked: 0 };
  const prefix = `dm_${String(conversationId)}_`;
  try {
    const q = query(
      collection(db, "notifications"),
      where("to", "==", uid),
      where("read", "==", false),
      fsLimit(400)
    );
    const snap = await getDocs(q);
    if (snap.empty) return { ok: true, marked: 0 };
    const targets = snap.docs.filter(
      (d) => d.data()?.type === "dm" && String(d.id).startsWith(prefix),
    );
    if (targets.length === 0) return { ok: true, marked: 0 };
    const batch = writeBatch(db);
    targets.forEach((d) => batch.update(d.ref, { read: true }));
    await batch.commit();
    return { ok: true, marked: targets.length };
  } catch (err) {
    logWarn(`markDmNotificationsRead(${conversationId})`, err);
    return { ok: false, marked: 0 };
  }
}

// ─── DEEP-LINK ────────────────────────────────────────────────────────────
/**
 * Notification.link alanını global hash/route sistemine uygular.
 * Mevcut codebase `#clip/{id}` tarzı hash route kullanıyor olabilir;
 * yoksa bu helper no-op gibi davranır (href set ederek routing'e delege).
 */
export function navigateToNotification(notif?: Bildirim | null) {
  if (!notif || typeof window === "undefined") return;
  const link = String(notif.link || "").trim();
  if (!link) {
    // Legacy fallback — eski bildirim şemasında postId/clipId vardı.
    // 🔴 ESKİDEN ŞEMA SEÇİMİ YOKTU: hangi alandan gelirse gelsin `/c/{id}`
    // yazılıyordu. `postId` taşıyan eski bir bildirim böylece KLİP rotasına
    // gidiyor, `clips/{postId}` boş dönüyor ve kullanıcı "içerik bulunamadı"
    // kartıyla karşılaşıyordu (ölçüldü 2026-08-24).
    // Artık kimlik HANGİ alandan geldiyse şema ondan seçilir; `contentType`
    // varsa o kazanır (en açık sinyal).
    const tur = String(notif.contentType || "").trim();
    let harf: "c" | "p" | null = null;
    let contentId: string | null = null;
    if (notif.contentId) {
      contentId = String(notif.contentId);
      harf = tur === "post" ? "p" : tur === "clip" ? "c" : null;
    }
    if (!contentId && notif.clipId) { contentId = String(notif.clipId); harf = "c"; }
    if (!contentId && notif.postId) { contentId = String(notif.postId); harf = "p"; }
    // `contentId` var ama `contentType` yoksa eski davranış korunur (klip):
    // o kohortta contentId'yi yazan tek yol klip hattıydı.
    if (contentId) {
      window.location.assign(`/${harf || "c"}/${encodeURIComponent(contentId)}`);
    }
    return;
  }
  try {
    if (link.startsWith("#")) {
      // Backend bazı bildirimleri hash şemasıyla yazıyor (#user/{uid},
      // #place/{placeId}, #clip/{id}, #post/{id}) ama uygulamada bu hash'leri
      // çözen bir router YOK → gerçek rotaya çevir (resolvePathToPage işler).
      const h = link.slice(1);
      const slash = h.indexOf("/");
      const kind = slash === -1 ? h : h.slice(0, slash);
      const id = slash === -1 ? "" : h.slice(slash + 1);
      if (id) {
        // BUG FIX (2026-07-01) — yorum/reply/mention bildirimleri
        // `#clip/{clipId}?cid={commentId}[&rid={replyId}]` formatında
        // yazılıyor (notifyClipComment/notifyClipReply/notifyMention).
        // Eskiden `id` (clipId+"?cid=..." bütünü) TEK PARÇA encodeURIComponent
        // edilip `/c/{encodedWhole}` üretiliyordu → "?" ve "=" da escape
        // edildiğinden path hiçbir rotayla eşleşmiyordu, tıklama hiçbir yere
        // gitmiyordu. Şimdi query kısmı ayrıştırılıp gerçek query-string
        // olarak korunuyor (clipId route'u doğru eşleşir; cid/rid ileride
        // yorum-anchor'ı için hazır kalır).
        const qIdx = id.indexOf("?");
        const rawId = qIdx === -1 ? id : id.slice(0, qIdx);
        const qs = qIdx === -1 ? "" : id.slice(qIdx);
        const enc = encodeURIComponent(rawId);
        if (kind === "user") { window.location.assign(`/${enc}${qs}`); return; }       // bare profil
        if (kind === "place") { window.location.assign(`/place/${enc}${qs}`); return; }
        if (kind === "clip") { window.location.assign(`/c/${enc}${qs}`); return; }
        if (kind === "post") { window.location.assign(`/p/${enc}${qs}`); return; }
      }
      // Bilinmeyen hash → eski davranış (no-op'a yakın).
      window.location.hash = h;
    } else if (link.startsWith("/")) {
      window.location.assign(link);
    } else {
      window.location.hash = link;
    }
  } catch (err) {
    logWarn("navigateToNotification", err);
  }
}
